"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useReassignSubTasks = void 0;
const react_1 = require("react");
const user_ui_1 = require("@sierralabs/user-ui");
const workflow_core_1 = require("@sierralabs/workflow-core");
const useStateNodeByStatus_1 = require("../../hooks/useStateNodeByStatus");
const CreateSubTasksPlugin_1 = require("../../plugins/services/CreateSubTasksPlugin");
const CheckSubTasksPlugin_1 = require("../../plugins/transition-conditions/CheckSubTasksPlugin");
const useFindTaskStatusLogs_1 = require("./useFindTaskStatusLogs");
const useTaskWorkflow_1 = require("./useTaskWorkflow");
function compareStatus(a, b) {
    // parse status for cases where workflow was renamed (e.g. status log)
    return a === b || (0, workflow_core_1.parseTaskStatus)(a) === (0, workflow_core_1.parseTaskStatus)(b);
}
function useReassignSubTasks(task) {
    var _a;
    const { user } = (0, user_ui_1.useUserContext)();
    const workflow = (0, useTaskWorkflow_1.useTaskWorkflow)(task);
    const stateNode = (0, useStateNodeByStatus_1.useStateNodeByStatus)(task.status, workflow);
    const { statusLogList, findStatusLogs } = (0, useFindTaskStatusLogs_1.useFindTaskStatusLogs)(task.id);
    const isAdmin = ((user === null || user === void 0 ? void 0 : user.groups) || []).some(group => group.name === 'Admin');
    const isAssigned = !!(user && task.user && task.user.id === user.id);
    const hasPermission = isAdmin || isAssigned;
    const statusLog = (_a = statusLogList === null || statusLogList === void 0 ? void 0 : statusLogList.data) === null || _a === void 0 ? void 0 : _a[0];
    const didTransition = !!(user &&
        statusLog &&
        statusLog.createdBy.id === user.id &&
        compareStatus(statusLog.status, task.status));
    // groups from createSubTasks action
    const groupIds = (0, react_1.useMemo)(() => {
        const groupIds = [];
        const invokes = !stateNode
            ? []
            : Array.isArray(stateNode.invoke)
                ? stateNode.invoke
                : stateNode.invoke
                    ? [stateNode.invoke]
                    : [];
        for (const invoke of invokes) {
            if (invoke.src === CreateSubTasksPlugin_1.CreateSubTasksPlugin.src && invoke.data) {
                const { data } = invoke;
                const allIds = data.groupIds || (data.groups || []).map(g => g === null || g === void 0 ? void 0 : g.id);
                const ids = allIds.filter((id) => !!id);
                groupIds.push(...ids);
            }
        }
        return groupIds;
    }, [stateNode]);
    // final subTask paths: get paths from checkSubTasks params
    const subTaskPaths = (0, react_1.useMemo)(() => {
        const paths = [];
        for (const event of (stateNode === null || stateNode === void 0 ? void 0 : stateNode.events) || []) {
            for (const transition of event.transitions || []) {
                for (const condition of transition.conditions || []) {
                    const params = condition.params;
                    if (condition.type === CheckSubTasksPlugin_1.CheckSubTasksPlugin.type &&
                        params &&
                        params.path) {
                        paths.push(params.path);
                    }
                }
            }
        }
        return paths;
    }, [stateNode]);
    // check from configured paths instead of workflow final state type
    const isSubTaskFinalized = (0, react_1.useCallback)((subTask) => {
        return subTaskPaths.some(targetPath => {
            return compareStatus(targetPath, subTask.status);
        });
    }, [subTaskPaths]);
    // can reassign subTask if:
    // - didTransition: user transitioned the main task and assigned subTasks
    // - hasPermission: user has admin role or is assigned to the main task
    // - subTask is not yet finalized
    const canReassign = (0, react_1.useCallback)((subTask) => {
        return (didTransition || hasPermission) && !isSubTaskFinalized(subTask);
    }, [didTransition, hasPermission, isSubTaskFinalized]);
    // if no permissions, get latest status log for didTransition check
    (0, react_1.useEffect)(() => {
        !hasPermission && findStatusLogs({ limit: 1 });
    }, [hasPermission, findStatusLogs]);
    return {
        groupIds,
        didTransition,
        hasPermission,
        canReassign,
        isSubTaskFinalized
    };
}
exports.useReassignSubTasks = useReassignSubTasks;
//# sourceMappingURL=useReassignSubTasks.js.map